import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { TCLIconImage } from '../components/TCLMark';

const IVORY = '#FAF8F3';
const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const WHAT_YOU_RECEIVE = [
  'Reflections on Cultural Fluency and leading across difference',
  'Selected essays from Promise Monday',
  'Practical insight on the CFQ™ framework and its four domains',
  'Occasional links to articles, the CFQ assessment, and relevant resources',
];

export default function SubscribePage() {
  const [email, setEmail]         = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus]       = useState('idle');
  const [message, setMessage]     = useState('');
  const [showMore, setShowMore]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/cfq/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, source: 'subscribe-page' }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message || 'Check your inbox to confirm your subscription.');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Subscribe — Third Culture Leadership</title>
        <meta name="description" content="Join the Third Culture Leadership mailing list for thoughtful essays and practical insights on Cultural Fluency and leading across difference." />
        <meta property="og:title" content="Subscribe — Third Culture Leadership" />
        <meta property="og:description" content="Thoughtful reflections on Cultural Fluency, leadership across difference, and the ideas behind Third Culture Leadership." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: NAVY }}>

        {/* ── Minimal header ─────────────────────────────── */}
        <header style={{ borderBottom: '1px solid rgba(201,147,22,0.07)' }}>
          <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <TCLIconImage size={28} className="opacity-75 group-hover:opacity-100 transition-opacity" />
              <span className="text-[10px] tracking-[0.22em] uppercase font-sans"
                style={{ color: 'rgba(250,248,243,0.38)' }}>
                Third Culture Leadership
              </span>
            </Link>
            <Link to="/articles"
              className="text-[10px] tracking-[0.16em] uppercase hover:opacity-80 transition-opacity"
              style={{ color: 'rgba(201,147,22,0.45)' }}>
              Read first
            </Link>
          </div>
        </header>

        {/* ── Main ── */}
        <main className="max-w-xl mx-auto px-6 pt-14 pb-24">

          {/* Eyebrow */}
          <p className="text-[9px] tracking-[0.35em] uppercase mb-6 font-sans"
            style={{ color: 'rgba(201,147,22,0.5)' }}>
            Third Culture Leadership
          </p>

          {/* Heading */}
          <h1 className="font-serif font-light mb-5 leading-tight"
            style={{
              fontSize: 'clamp(1.9rem, 4.5vw, 2.75rem)',
              color: IVORY,
              letterSpacing: '-0.01em',
            }}>
            Start your learning journey.
          </h1>

          {/* Short supporting note */}
          <p className="text-base font-light leading-relaxed mb-8"
            style={{
              color: 'rgba(250,248,243,0.45)',
              fontFamily: 'Georgia, "Times New Roman", serif',
            }}>
            Join the mailing list to receive thoughtful reflections, essays and practical
            insights on Cultural Fluency and Third Culture Leadership.
          </p>

          {/* ── Form ── */}
          {status === 'success' ? (
            <div className="rounded-2xl p-9 text-center"
              style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(201,147,22,0.1)' }}
              data-testid="subscribe-page-success">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                style={{ background: 'rgba(34,197,94,0.1)' }}>
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="font-serif text-xl font-light mb-2" style={{ color: IVORY }}>
                Confirm your email
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(250,248,243,0.35)' }}>
                {message}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="subscribe-page-form"
              className="rounded-2xl p-6"
              style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.07)' }}>

              {/* First name (optional — appears inline on wider screens) */}
              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <input
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  className="flex-1 rounded-xl px-4 py-3.5 text-sm focus:outline-none transition-colors"
                  style={{
                    background: 'rgba(250,248,243,0.05)',
                    border: '1px solid rgba(250,248,243,0.08)',
                    color: IVORY,
                  }}
                  data-testid="subscribe-page-firstname"
                />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email address"
                  required
                  className="flex-[1.4] rounded-xl px-4 py-3.5 text-sm focus:outline-none transition-colors"
                  style={{
                    background: 'rgba(250,248,243,0.05)',
                    border: '1px solid rgba(250,248,243,0.1)',
                    color: IVORY,
                  }}
                  data-testid="subscribe-page-email"
                />
              </div>

              {status === 'error' && (
                <p className="text-red-400 text-sm mb-3">{message}</p>
              )}

              <button
                type="submit"
                disabled={!email || status === 'loading'}
                className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: GOLD, color: NAVY }}
                data-testid="subscribe-page-submit">
                {status === 'loading'
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing…</>
                  : <>Start the Journey <ArrowRight className="w-4 h-4" /></>
                }
              </button>

              {/* Microcopy */}
              <p className="text-center text-[11px] mt-3"
                style={{ color: 'rgba(250,248,243,0.18)' }}>
                No spam. Just thoughtful writing and practical insight.
              </p>
            </form>
          )}

          {/* ── Optional secondary section ─────────────── */}
          <div className="mt-10">
            <button
              onClick={() => setShowMore(v => !v)}
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] transition-opacity hover:opacity-80"
              style={{ color: 'rgba(201,147,22,0.4)' }}>
              <span
                className="inline-block transition-transform"
                style={{ transform: showMore ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                ›
              </span>
              What you'll receive
            </button>

            {showMore && (
              <ul className="mt-4 space-y-2.5 pl-4"
                style={{ borderLeft: '1px solid rgba(201,147,22,0.12)' }}>
                {WHAT_YOU_RECEIVE.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed"
                    style={{ color: 'rgba(250,248,243,0.35)' }}>
                    <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: 'rgba(201,147,22,0.35)' }} />
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Footer attribution ──────────────────────── */}
          <div className="mt-16 pt-6" style={{ borderTop: '1px solid rgba(250,248,243,0.05)' }}>
            <p className="text-xs" style={{ color: 'rgba(250,248,243,0.18)' }}>
              Written by{' '}
              <Link to="/about"
                className="underline underline-offset-4 hover:opacity-80 transition-opacity"
                style={{ color: 'rgba(201,147,22,0.45)', textDecorationColor: 'rgba(201,147,22,0.18)' }}>
                Promise Monday
              </Link>
              {' '}— Paediatrician · Author · Founder, Third Culture Leadership
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
